import React, { Component, Fragment } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  FlatList,
  ListRenderItemInfo,
  TextStyle,
} from 'react-native';

import ClickAway from '../uiold/ClickAway';
import { themeColors } from '../uiold/Constants';
import Hoverable from '../uiold/Hoverable';
import Icon from '../uiold/Icon';
import Text from '../uiold/Text';

type State = {
  showPicker: boolean;
};

type Props<T> = {
  displayLabel?: string;
  labelStyle?: TextStyle;
  options: Array<T>;
  selectedItem: T | null | undefined;
  getItemLabel: (item: T) => string;
  getItemId?: (item: T) => string;
  renderIcon?: (item: T) => React.ReactElement;
  onChange?: (item: T) => void;
  style?: StyleSet;
  isDisabled?: boolean;
  testID?: string;
};

export default class Picker<T> extends Component<Props<T>, State> {
  state: State = {
    showPicker: false,
  };

  render() {
    let {
      displayLabel,
      selectedItem,
      options,
      getItemLabel,
      style,
      isDisabled,
      labelStyle,
      testID,
    } = this.props;
    let { showPicker } = this.state;
    let label =
      displayLabel || (selectedItem ? getItemLabel(selectedItem) : '');
    return (
      <Fragment>
        <Hoverable
          render={(isHovered, eventHandlers) => (
            <TouchableOpacity
              testID={testID}
              onPress={this._togglePicker}
              activeOpacity={0.6}
              disabled={isDisabled || showPicker}
            >
              <View
                style={[styles.root, isHovered && styles.hoveredRoot, style]}
                {...eventHandlers}
              >
                <Text
                  style={[labelStyle, styles.label]}
                  numberOfLines={1}
                  title={label}
                >
                  {label}
                </Text>
                <Icon
                  name={showPicker ? 'arrowUp' : 'arrowDown'}
                  size="M"
                  style={[
                    styles.iconArrowDown,
                    showPicker && styles.iconArrowUp,
                  ]}
                />
              </View>
            </TouchableOpacity>
          )}
        />
        {showPicker && (
          <ClickAway onClickAway={this._togglePicker}>
            <View style={styles.picker}>
              <FlatList
                keyExtractor={this._keyExtractor}
                data={options}
                renderItem={this._renderItem}
              />
            </View>
          </ClickAway>
        )}
      </Fragment>
    );
  }

  _renderItem = ({ item }: ListRenderItemInfo<T>) => {
    let onPress = () => {
      this.props.onChange?.(item);
      this.setState({ showPicker: false });
    };
    let isActive = this.props.selectedItem === item;
    let label = this.props.getItemLabel(item);
    let itemId = this.props.getItemId ? this.props.getItemId(item) : '';

    return (
      <Hoverable
        render={(isHovered, eventHandlers) => (
          <TouchableOpacity
            testID={`picker-change-${itemId.toLowerCase()}`}
            onPress={onPress}
            activeOpacity={0.6}
          >
            <View
              style={[styles.item, isHovered && styles.itemHovered]}
              {...eventHandlers}
            >
              {this.props.renderIcon?.(item)}
              <Text style={isActive && styles.itemTextActive} numberOfLines={1}>
                {label}
              </Text>
            </View>
          </TouchableOpacity>
        )}
      />
    );
  };

  _togglePicker = () => {
    this.setState({
      showPicker: !this.state.showPicker,
    });
  };

  _keyExtractor = (item: T, i: number) => String(i);
}

let itemHeight = 30;

let transitionStyle = {
  transitionDuration: '0.25s',
  transitionTimingFunction: 'ease-out',
};

let styles = StyleSheet.create({
  root: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 34,
    paddingLeft: 12,
    paddingRight: 6,
    backgroundColor: themeColors.white,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 3,
    borderColor: themeColors.borderGrey,
    maxWidth: 200,
  },
  hoveredRoot: {
    backgroundColor: themeColors.lightGrey,
  },
  picker: {
    position: 'absolute',
    marginTop: 10,
    paddingVertical: 5,
    backgroundColor: themeColors.white,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: themeColors.borderGrey,
    borderRadius: 3,
    top: 34,
    left: 0,
    width: 200,
  },
  label: {
    flex: 1,
  },
  item: {
    height: itemHeight,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingHorizontal: 10,
  },
  itemHovered: {
    backgroundColor: themeColors.withOpacity.blue8,
  },
  itemTextActive: {
    fontWeight: '700',
    color: themeColors.blue,
  },
  iconArrowDown: {
    marginLeft: 6,
    transitionProperty: 'transform',
    ...transitionStyle,
  },
  iconArrowUp: {
    transform: [{ rotate: '-0.5turn' }],
  },
});
